import React from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Brain, FileText, Copy, Check } from "lucide-react";
import { useDesignSystem, cn } from '../../designSystem';

const NoteTab = ({
  note,
  chosenTemplate,
  setChosenTemplate,
  templates,
  generateNote,
  isGeneratingNote,
  convertMarkdownToHtml,
  setShowResourceSuggestions,
  isTabletView,
  isDesktopView,
  isLandscape
}) => {
  const { colors, typography, componentStyles, effects, shadows } = useDesignSystem();

  // Desktop-visning
  if (isDesktopView) {
    return (
      <Card className={cn(
        "h-[calc(100vh-220px)]",
        componentStyles.card.glass,
        "overflow-hidden flex flex-col"
      )}>
        <CardHeader className="pb-3">
          <CardTitle className="flex justify-between items-center">
            <span className={cn(colors.neutral.textPrimary)}>Notat</span>
            <div className="flex items-center space-x-2">
              <Select value={chosenTemplate} onValueChange={setChosenTemplate}>
                <SelectTrigger className={cn(
                  "w-[200px]",
                  colors.primary.border,
                  colors.primary.focusRing,
                  "dark:border-neutral-700 dark:bg-neutral-900"
                )}>                    
                  <div className={cn(
                    "truncate",
                    colors.neutral.textPrimary
                  )}>
                    <SelectValue placeholder="Velg mal" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(templates).map((key) => (
                    <SelectItem key={key} value={key}>{key}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={generateNote}
                className={cn(
                  componentStyles.button.base,
                  componentStyles.button.primary,
                  shadows.sm
                )}
                disabled={isGeneratingNote}
              >
                <FileText className="mr-2 h-4 w-4" />
                Generer
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="h-[calc(100%-85px)] p-6 flex flex-col gap-6">
          <div className="flex-1 border-none rounded-md overflow-hidden relative">
            {note && (
              <Button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(note);
                    const button = document.getElementById('copyButton');
                    const copyIcon = document.getElementById('copyIcon');
                    const checkIcon = document.getElementById('checkIcon');
                    copyIcon.classList.add('hidden');
                    checkIcon.classList.remove('hidden');
                    button.classList.add('text-blue-600');
                    setTimeout(() => {
                      copyIcon.classList.remove('hidden');
                      checkIcon.classList.add('hidden');
                      button.classList.remove('text-blue-600');
                    }, 2000);
                  } catch (err) {
                    console.error('Kunne ikke kopiere til utklippstavlen:', err);
                  }
                }}
                id="copyButton"
                variant="ghost"
                size="icon"
                className={cn(
                  "absolute top-2 right-2 h-8 w-8",
                  "hover:bg-blue-50 dark:hover:bg-neutral-800",
                  effects.transition,
                  "z-10"
                )}
              >
                <Copy id="copyIcon" className="h-4 w-4" />
                <Check id="checkIcon" className="h-4 w-4 hidden" />
              </Button>
            )}
            <ScrollArea className={cn(
              componentStyles.scrollArea.base,
              "h-full"
            )}>
              {note ? (
                <div className={cn(
                  "p-6",
                  typography.bodyLg,
                  "whitespace-pre-wrap w-full"
                )} dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(note) }} />
              ) : (
                <div className="flex flex-col items-center justify-center min-h-[400px]">
                  <Brain className="h-12 w-12 mb-4 text-gray-300 dark:text-neutral-600" />
                  <p className="text-center text-gray-400 dark:text-neutral-500 italic">
                    Klar til å generere notat<br />
                    Velg mal og klikk på "Generer"
                  </p>
                </div>
              )}
            </ScrollArea>
          </div>
          <div className={cn(
            colors.primary.light,
            "rounded-lg p-4 border",
            colors.primary.border,
            "mt-6"
          )}>
            <h3 className={cn(
              typography.small,
              typography.medium,
              colors.primary.text,
              "mb-2"
            )}>
              Ressursbank
            </h3>
            <p className={cn(
              typography.small,
              colors.primary.text,
              "mb-4"
            )}>
              Finn relevante ressurser basert på konsultasjonens innhold
            </p>
            <Button 
              onClick={() => setShowResourceSuggestions(true)} 
              className={cn(
                componentStyles.button.base,
                componentStyles.button.primary,
                !note && "opacity-50 cursor-not-allowed"
              )}
              disabled={!note}
            >
              <Brain className="mr-2 h-4 w-4" />
              Foreslå ressurser
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Tablet-visning
  if (isTabletView) {
    return (
      <Card className={cn(
        "h-[calc(100vh-220px)]",
        componentStyles.card.glass,
        "overflow-hidden flex flex-col"
      )}>
        <CardHeader className="p-3">
          <CardTitle className="flex justify-between items-center">
            <span className={cn(colors.neutral.textPrimary)}>Notat</span>
            <div className="flex items-center space-x-1">
              <Select value={chosenTemplate} onValueChange={setChosenTemplate}>
                <SelectTrigger className={cn(
                  "w-[150px]",
                  colors.primary.border,
                  colors.primary.focusRing,
                  "dark:border-neutral-700 dark:bg-neutral-900"
                )}>                    
                  <div className={cn(
                    "truncate",
                    colors.neutral.textPrimary
                  )}>
                    <SelectValue placeholder="Velg mal" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(templates).map((key) => (
                    <SelectItem key={key} value={key}>{key}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={generateNote}
                className={cn(
                  componentStyles.button.base,
                  componentStyles.button.primary,
                  shadows.sm,
                  "px-2 text-xs"
                )}
                disabled={isGeneratingNote}
              >
                <FileText className="mr-2 h-3 w-3" />
                Generer
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="h-[calc(100%-85px)] p-4 flex flex-col gap-4">
          <div className="flex-1 border-none rounded-md overflow-hidden relative">
            {note && (
              <Button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(note);
                    const copyButton = document.getElementById('copyButtonTablet');
                    const copyIcon = document.getElementById('copyIconTablet');
                    const checkIcon = document.getElementById('checkIconTablet');
                    copyIcon.classList.add('hidden');
                    checkIcon.classList.remove('hidden');
                    copyButton.classList.add('text-blue-600');
                    setTimeout(() => {
                      copyIcon.classList.remove('hidden');
                      checkIcon.classList.add('hidden');
                      copyButton.classList.remove('text-blue-600');
                    }, 2000);
                  } catch (err) {
                    console.error('Kunne ikke kopiere til utklippstavlen:', err);
                  }
                }}
                id="copyButtonTablet"
                variant="ghost"
                size="icon"
                className={cn(
                  "absolute top-2 right-2 h-8 w-8",
                  "hover:bg-blue-50 dark:hover:bg-neutral-800",
                  effects.transition,
                  "z-10"
                )}
              >
                <Copy id="copyIconTablet" className="h-4 w-4" />
                <Check id="checkIconTablet" className="h-4 w-4 hidden" />
              </Button>
            )}
            <ScrollArea className={cn(
              componentStyles.scrollArea.base,
              "h-full"
            )}>
              {note ? (
                <div className={cn(
                  "p-4",
                  typography.bodyLg,
                  "whitespace-pre-wrap w-full"
                )} dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(note) }} />
              ) : (
                <div className="flex flex-col items-center justify-center min-h-[400px]">
                  <Brain className="h-12 w-12 mb-4 text-gray-300 dark:text-neutral-600" />
                  <p className="text-center text-gray-400 dark:text-neutral-500 italic">
                    Klar til å generere notat<br />
                    Velg mal og klikk på "Generer"
                  </p>
                </div>
              )}
            </ScrollArea>
          </div>
          <div className={cn(
            colors.primary.light,
            "rounded-lg p-4 border",
            colors.primary.border
          )}>
            <h3 className={cn(
              typography.small,
              typography.medium,
              colors.primary.text,
              "mb-2"
            )}>
              Ressursbank
            </h3>
            <p className={cn(
              typography.small,
              colors.primary.text,
              "mb-2"
            )}>
              Finn relevante ressurser basert på konsultasjonens innhold
            </p>
            <Button 
              onClick={() => setShowResourceSuggestions(true)} 
              className={cn(
                componentStyles.button.base,
                componentStyles.button.primary,
                "text-xs",
                !note && "opacity-50 cursor-not-allowed"
              )}
              disabled={!note}
            >
              <Brain className="mr-2 h-3 w-3" />
              Foreslå ressurser
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Mobil-visning (default)
  return (
    <Card className={cn(
      componentStyles.card.glass,
      "border shadow-sm"
    )}>
      <CardHeader className="pb-1 px-3 pt-2">
        <div className="flex items-center justify-between">
          <Select value={chosenTemplate} onValueChange={setChosenTemplate}>
            <SelectTrigger className={cn(
              "w-[130px]",
              colors.neutral.border,
              "dark:bg-neutral-800 h-8"
            )}>
              <div className={cn(
                "truncate",
                colors.neutral.textPrimary
              )}>
                <SelectValue placeholder="Velg mal" />
              </div>
            </SelectTrigger>
            <SelectContent className="max-w-[200px] w-[calc(100vw-80px)]" align="start" sideOffset={4}>
              {Object.keys(templates).map((key) => (
                <SelectItem key={key} value={key} className="truncate">
                  {key}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={generateNote}
            className={cn(
              componentStyles.button.base,
              componentStyles.button.primary,
              componentStyles.button.sizes.sm,
              isGeneratingNote && componentStyles.button.states.disabled
            )}
            disabled={isGeneratingNote}
          >
            {isGeneratingNote ? (
              <>
                <div className={cn(
                  "mr-1 h-3 w-3 border-2 border-white border-t-transparent rounded-full",
                  effects.spin
                )}></div>
                <span className="text-xs">Genererer...</span>
              </>
            ) : (
              <>
                <FileText className="mr-1 h-4 w-4" />
                <span className="text-xs">Generer</span>
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-3 pt-1">
        <div className="relative">
          {note ? (
            <div className="relative">
              <div className={cn(
                "h-[calc(100vh-370px)] overflow-y-auto rounded-md border",
                colors.neutral.border,
                colors.neutral.bg,
                "dark:bg-neutral-800 p-3"
              )}>
                <div 
                  className={cn(
                    typography.bodyLg,
                    "whitespace-pre-wrap"
                  )}
                  dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(note) }}
                />
              </div>
              <div className="absolute top-2 right-2">
                <Button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(note);
                      const button = document.getElementById('copyButtonMobile');
                      if (button) {
                        button.innerHTML = '<svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>';
                        setTimeout(() => {
                          button.innerHTML = '<svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>';
                        }, 2000);
                      }
                    } catch (err) {
                      console.error('Kunne ikke kopiere til utklippstavlen:', err);
                    }
                  }}
                  id="copyButtonMobile"
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "h-8 w-8",
                    effects.glassmorphism
                  )}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : (
            <div className={cn(
              "h-[calc(100vh-370px)] flex flex-col items-center justify-center rounded-md border",
              colors.neutral.border,
              colors.neutral.bg,
              "dark:bg-neutral-800"
            )}>
              <Brain className={cn(
                "h-10 w-10 mb-3",
                colors.neutral.textTertiary
              )} />
              <p className={cn(
                "text-center",
                colors.neutral.textTertiary,
                "italic text-sm"
              )}>
                Velg mal og generer notat
              </p>
            </div>
          )}
        </div>

        <div className={cn(
          "mt-2",
          colors.primary.light,
          "rounded-lg p-2 border",
          colors.primary.border
        )}>
          <label className={cn(
            "block text-xs font-medium",
            colors.primary.text,
            "mb-1"
          )}>
            Ressursbank
          </label>
          <div className="h-16 flex flex-col">
            <p className={cn(
              "text-xs",
              colors.primary.text,
              "mb-1"
            )}>
              Finn relevante ressurser basert på konsultasjonens innhold
            </p>
            <Button
              onClick={() => setShowResourceSuggestions(true)}
              className={cn(
                "w-full mt-auto",
                componentStyles.button.base,
                componentStyles.button.primary,
                !note && "opacity-50",
                componentStyles.button.sizes.sm
              )}
              disabled={!note}
            >
              <Brain className="mr-2 h-3.5 w-3.5" />
              <span className="text-xs">Foreslå ressurser</span>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default NoteTab;